import type React from 'react'
import { useRef } from 'react'
import { Vertical, GraviGrid, MaterialIcon, Texto, GraviButton } from '@gravitate-js/excalibrr'
import type {
  GridApi,
  ColDef,
  ICellRendererParams,
  RowHeightParams,
  GetRowIdParams,
  SelectionChangedEvent,
} from 'ag-grid-community'
import type { components } from '../../../../../../types/api/v1'
import { getRowHeight } from '../utils/manageMatchUtils'

type BolCandidate = components['schemas']['BolCandidate']
type InvoiceCandidate = components['schemas']['InvoiceCandidate']
type ManageMatchItem = BolCandidate | InvoiceCandidate

interface ManageMatchGridProps {
  searchType: 'invoice' | 'bol'
  gridData: ManageMatchItem[]
  columns: ColDef<ManageMatchItem>[]
  isLoading: boolean
  error: Error | null
  onSelectionChange: (selectedRows: ManageMatchItem[]) => void
  onRefetch: () => void
  gridApiRef?: React.MutableRefObject<GridApi | null>
}

export function ManageMatchGrid({
  searchType,
  gridData,
  columns,
  isLoading,
  error,
  onSelectionChange,
  onRefetch,
  gridApiRef,
}: ManageMatchGridProps) {
  const gridRef = useRef<GridApi>(null)

  const getRowStyle = (params: ICellRendererParams<ManageMatchItem>) => {
    if (params.data?.inMatch) {
      return {
        background: 'var(--theme-elevated)',
        borderBottom: '2px solid var(--theme-separator)',
      }
    }
    return {}
  }

  const getRowHeightDynamic = (params: RowHeightParams<ManageMatchItem>) => {
    const data = params.data
    let productCount = 1 // Default to 1 product

    if (searchType === 'bol' && data?.products && Array.isArray(data.products)) {
      productCount = data.products.length
    } else if (searchType === 'invoice' && data?.details && Array.isArray(data.details)) {
      // Count unique products in details
      const uniqueProducts = new Set()
      data.details.forEach((detail: { product?: { internal_name?: string; source_name?: string } }) => {
        if (detail.product?.internal_name || detail.product?.source_name) {
          uniqueProducts.add(detail.product.internal_name || detail.product.source_name)
        }
      })
      productCount = uniqueProducts.size || 1
    }

    // Base height + extra height per additional product - matching original exactly
    const baseHeight = 35
    const heightPerProduct = 16
    return Math.max(baseHeight, baseHeight + (productCount - 1) * heightPerProduct)
  }

  const getRowId = (params: GetRowIdParams<ManageMatchItem>) => {
    const data = params.data
    // Use the actual ID field from the schema, fallback to number if needed
    return data?.id || data?.invoice_id || data?.bol_id || data?.invoice_number || data?.bol_number || params.node.id
  }

  const handleSelectionChanged = (params: SelectionChangedEvent<ManageMatchItem>) => {
    const newSelectedRows = params.api.getSelectedRows()
    onSelectionChange(newSelectedRows)
  }

  if (error) {
    return (
      <Vertical padding={24} verticalCenter horizontalCenter gap={16}>
        <MaterialIcon icon='error' size={48} style={{ color: 'var(--theme-error)' }} />
        <Texto category='h5' weight='600'>
          Failed to load {searchType === 'bol' ? 'BOLs' : 'invoices'}
        </Texto>
        <Texto category='p2' appearance='hint' textAlign='center'>
          {error?.message || 'An error occurred while fetching data'}
        </Texto>
        <GraviButton onClick={onRefetch}>Try Again</GraviButton>
      </Vertical>
    )
  }

  if (!columns || columns.length === 0) {
    return (
      <Vertical padding={24} verticalCenter horizontalCenter gap={16}>
        <Texto category='p2' appearance='hint'>
          Loading columns...
        </Texto>
      </Vertical>
    )
  }

  return (
    <Vertical flex={1} style={{ overflow: 'hidden' }}>
      <GraviGrid
        externalRef={gridApiRef}
        columnDefs={columns}
        rowData={gridData}
        loading={isLoading}
        fullWidthSearchBar
        agPropOverrides={{
          rowSelection: 'multiple',
          suppressRowClickSelection: false,
          headerHeight: 40,
          domLayout: 'normal',
          rowGroupPanelShow: 'never',
          // Enable row grouping and sorting
          enableRangeSelection: true,
          enableCellTextSelection: true,
          // Style items in match
          getRowStyle,
          // Dynamic row height based on product count
          getRowHeight: getRowHeightDynamic,
          // Auto-size columns
          autoSizeStrategy: {
            type: 'fitGridWidth',
            defaultMinWidth: 100,
          },
          // Use proper ID field as row ID to avoid duplicates
          getRowId,
          onSelectionChanged: handleSelectionChanged,
        }}
        style={{ height: '100%' }}
      />
    </Vertical>
  )
}
